/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */

// React and hooks
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

// React Hook Form
import { Controller, useFieldArray, useForm } from 'react-hook-form';

// Chakra UI components
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Menu,
  Separator,
  Spinner,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react';

// Icons
import { CaretDown, Plus, Trash } from '@phosphor-icons/react';

// API
import { fetchAPI } from '@/api/fetch';
import {
  apiCreateQuestion,
  apiGetQuestionBankCategories,
  apiUpdateQuestion,
  CreateQuestionPayload,
  UpdateQuestionPayload
} from '@/api/services/question_bank.service';

// Components
import { RequiredFieldLabel } from '@/components/forms/RequiredFieldLabel';
import { Field } from '@/components/ui/field';
import ImportQuestionBankDialog from './ImportQuestionBankDialog';
import { LoadingSkeleton } from '@/components/layout/LoadingSkeleton';

interface QuestionBankEditorProps {
  isLoading: boolean;
  onCancel: () => void;
  onSubmit: (questionBank: QuestionBank) => void;
  questionBank?: QuestionBank | null;
}

interface FormValues {
  category: string;
  jobTitle: string;
  description: string;
  questions: { value: string }[];
}

// Memoized question field component to prevent unnecessary re-renders
const QuestionField = memo(
  ({
    index,
    field,
    control,
    errors,
    isSubmitting,
    onRemove,
    canRemove
  }: {
    index: number;
    field: any;
    control: any;
    errors: any;
    isSubmitting: boolean;
    onRemove: (index: number) => void;
    canRemove: boolean;
  }) => (
    <Flex key={field.id} gap="12px" alignItems="center" alignContent="center" p="12px 24px">
      <Text fontSize="16px">{index + 1}.</Text>
      <Box flex="1">
        <Controller
          name={`questions.${index}.value`}
          disabled={isSubmitting}
          control={control}
          rules={{
            required: 'Question is required',
            minLength: {
              value: 5,
              message: 'Question must be at least 5 characters'
            }
          }}
          render={({ field: inputField }) => (
            <>
              <Input
                border="1px solid"
                borderColor={errors.questions?.[index]?.value ? 'error.500' : 'neutrals.200'}
                borderRadius="12px"
                fontSize="16px"
                {...inputField}
                placeholder="Enter your question here..."
              />
              {errors.questions?.[index]?.value && (
                <Text color="error.500" fontSize="14px" mt="4px">
                  {errors.questions[index].value.message}
                </Text>
              )}
            </>
          )}
        />
      </Box>
      {canRemove && (
        <IconButton
          variant="ghost"
          colorScheme="red"
          size="sm"
          disabled={isSubmitting}
          onClick={() => onRemove(index)}
          mt={1}
        >
          <Trash size={16} />
        </IconButton>
      )}
    </Flex>
  )
);

QuestionField.displayName = 'QuestionField';

function QuestionBankEditor({
  isLoading,
  onCancel,
  onSubmit,
  questionBank
}: QuestionBankEditorProps) {
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState<string>('');
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState<boolean>(false);
  const isEditing = questionBank !== undefined && questionBank !== null;

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    getValues,
    setValue,
    watch
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      category: '',
      jobTitle: '',
      description: '',
      questions: [{ value: '' }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions'
  });

  // Watch all form values to trigger reactivity
  const watchedValues = watch();

  // Memoized computations
  const isFormValid = useMemo(() => {
    const hasNoErrors = Object.keys(errors).length === 0;
    const allQuestionsValid = fields.every((_, index) => {
      const value = watchedValues.questions?.[index]?.value || '';
      return value.trim().length >= 5;
    });
    return hasNoErrors && allQuestionsValid && fields.length > 0;
  }, [errors, fields, watchedValues]);

  const canAddQuestion = useMemo(() => fields.length < 15, [fields.length]);

  useEffect(() => {
    // Reset form when questionBank changes
    if (isEditing) {
      reset({
        category: questionBank.category,
        jobTitle: questionBank.job_title,
        description: questionBank.description || '',
        questions:
          questionBank.questions.length > 0
            ? questionBank.questions.map((q) => ({ value: q.question }))
            : [{ value: '' }]
      });
    } else {
      reset({
        category: '',
        jobTitle: '',
        description: '',
        questions: [{ value: '' }]
      });
    }

    setSubmitError(null);
  }, [isEditing, questionBank]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const response = await fetchAPI({
        request: () => apiGetQuestionBankCategories()
      });

      if (response.success && response.data) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setSubmitError('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const handleAddNewCategory = useCallback(() => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      const updatedCategories = [...categories, newCategory.trim()];
      setCategories(updatedCategories);
      setNewCategory('');
    }
  }, [newCategory, categories]);

  const handleAddQuestion = useCallback(() => {
    if (canAddQuestion) {
      append({ value: '' });
    }
  }, [canAddQuestion, append]);

  const handleRemoveQuestion = useCallback(
    (index: number) => {
      if (fields.length > 1) {
        remove(index);
      }
    },
    [fields.length, remove]
  );

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
    setSubmitError(null);
  }, [reset, onCancel]);

  const handleImport = useCallback(
    (data: QuestionBankExtractionData) => {
      const { job_title, description, questions } = data;

      setValue('jobTitle', job_title);
      setValue('description', description || '');

      // Add imported questions to existing ones
      const existingQuestions = getValues('questions') || [];
      const importedQuestionValues = questions.map((question) => ({ value: question }));
      // Filter out empty placeholder if it's the only existing question
      const filteredExisting =
        existingQuestions.length === 1 && !existingQuestions[0].value.trim()
          ? []
          : existingQuestions;
      const combinedQuestions = [...filteredExisting, ...importedQuestionValues];

      // Limit to 15 questions
      const limitedQuestions = combinedQuestions.slice(0, 15);
      setValue('questions', limitedQuestions.length > 0 ? limitedQuestions : [{ value: '' }]);

      // Show warning if questions were truncated
      if (combinedQuestions.length > 15) {
        setSubmitError(
          `Import limited to 15 questions. ${
            combinedQuestions.length - 15
          } questions were excluded.`
        );
      } else {
        setSubmitError(null);
      }
    },
    [setValue, getValues]
  );

  const onSubmitForm = async (data: FormValues) => {
    setSubmitError(null);

    // Check if there are too many questions
    if (data.questions.length > 15) {
      setSubmitError('Maximum of 15 questions allowed');
      return;
    }

    // Validate questions
    const validQuestions = data.questions.filter((q) => q.value.trim().length >= 5);
    if (validQuestions.length === 0) {
      setSubmitError('Please add at least one valid question (5+ characters)');
      return;
    }

    const questionData = validQuestions.map((q) => ({ question: q.value.trim() }));

    try {
      setSubmitError(null);

      if (isEditing && questionBank) {
        const updatePayload: UpdateQuestionPayload = {
          category: data.category,
          job_title: data.jobTitle.trim(),
          description: data.description.trim() || undefined,
          questions: questionData
        };

        const response = await fetchAPI({
          request: () =>
            apiUpdateQuestion({
              questionId: questionBank.question_id,
              payload: updatePayload
            })
        });

        if (response.success) {
          const updatedQuestionBank = {
            ...questionBank,
            category: data.category,
            job_title: data.jobTitle.trim(),
            description: data.description.trim(),
            questions: questionData
          };
          onSubmit(updatedQuestionBank);
        } else {
          console.error('Failed to update question bank:', response.message);
          setSubmitError(response.message || 'Failed to update question bank');
        }
      } else {
        const createPayload: CreateQuestionPayload = {
          category: data.category,
          job_title: data.jobTitle.trim(),
          description: data.description.trim() || undefined,
          questions: questionData
        };

        const response = await fetchAPI({
          request: () => apiCreateQuestion({ payload: createPayload })
        });
        if (response.success && response.data) {
          onSubmit(response.data);
        } else {
          console.error('Failed to create question bank:', response.message);
          setSubmitError(response.message || 'Failed to create question bank');
        }
      }
    } catch (err) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} question bank:`, err);
      setSubmitError(`Failed to ${isEditing ? 'update' : 'create'} question bank`);
    }
  };

  return (
    <>
      <Box h="100%" overflowY="auto">
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Flex justify="space-between" align="center" mb="32px">
            <Stack align="start" gap="0">
              <Text fontSize="20px" fontWeight="bold" color="neutrals.900">
                Question Editor
              </Text>
              <Text fontSize="16px" color="neutrals.500">
                Edit your interview question set here.
              </Text>
            </Stack>
            <Button
              borderRadius="8px"
              bg="primary.900"
              color="neutrals.0"
              p="12px 16px"
              fontSize="16px"
              _hover={{ bg: 'primary.700' }}
              onClick={() => setIsImportDialogOpen(true)}
              disabled={isLoading || isSubmitting}
            >
              Import Questions
            </Button>
          </Flex>

          {isLoading ? (
            <LoadingSkeleton count={3} />
          ) : (
            <Stack gap={6} align="stretch" fontSize="16px">
              {/* Category Selection */}
              <Field
                label={<RequiredFieldLabel>Category</RequiredFieldLabel>}
                errorText={errors.category?.message}
              >
                {categoriesLoading ? (
                  <Flex align="center" gap={2}>
                    <Spinner size="sm" />
                    <Text>Loading categories...</Text>
                  </Flex>
                ) : (
                  <Stack w="100%" align="stretch" gap={2}>
                    <Controller
                      name="category"
                      disabled={isSubmitting}
                      control={control}
                      rules={{ required: 'Please select a category' }}
                      render={({ field }) => (
                        <Menu.Root positioning={{ placement: 'bottom-start' }}>
                          <Menu.Trigger asChild disabled={isSubmitting}>
                            <Button
                              variant="outline"
                              border="1px solid"
                              borderRadius="8px"
                              borderColor={errors.category ? 'error.500' : 'neutrals.200'}
                              w="80%"
                              textAlign="left"
                              fontSize="16px"
                            >
                              <Flex
                                justify="space-between"
                                align="center"
                                w="100%"
                                color={field.value ? 'neutrals.900' : 'neutrals.500'}
                              >
                                {field.value || 'Select a category'}
                                <CaretDown size={16} />
                              </Flex>
                            </Button>
                          </Menu.Trigger>
                          <Menu.Positioner>
                            <Menu.Content borderRadius="8px">
                              {categories.length > 0 && (
                                <>
                                  {categories.map((category) => (
                                    <Menu.Item
                                      key={category}
                                      value={category}
                                      cursor="pointer"
                                      fontSize="16px"
                                      onClick={() => field.onChange(category)}
                                    >
                                      {category}
                                    </Menu.Item>
                                  ))}
                                  <Menu.Separator />
                                </>
                              )}
                              <Menu.Item
                                value="new-category-input"
                                closeOnSelect={false}
                                _hover={{ bg: 'transparent' }}
                              >
                                <HStack w="100%" onClick={(e) => e.stopPropagation()}>
                                  <Input
                                    fontSize="16px"
                                    placeholder="Enter new category"
                                    value={newCategory}
                                    size="sm"
                                    onChange={(e) => setNewCategory(e.target.value)}
                                    onKeyDown={(e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter') {
                                        handleAddNewCategory();
                                        field.onChange(newCategory.trim());
                                      }
                                    }}
                                  />
                                  <IconButton
                                    variant="ghost"
                                    size="sm"
                                    color="primary.900"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddNewCategory();
                                      field.onChange(newCategory.trim());
                                    }}
                                    disabled={!newCategory.trim()}
                                  >
                                    <Plus size={16} weight="bold" />
                                  </IconButton>
                                </HStack>
                              </Menu.Item>
                            </Menu.Content>
                          </Menu.Positioner>
                        </Menu.Root>
                      )}
                    />
                  </Stack>
                )}
              </Field>

              {/* Job Title */}
              <Field
                label={<RequiredFieldLabel>Job Title</RequiredFieldLabel>}
                errorText={errors.jobTitle?.message}
              >
                <Controller
                  name="jobTitle"
                  control={control}
                  disabled={isSubmitting}
                  rules={{ required: 'Please enter a job title' }}
                  render={({ field }) => (
                    <Box px="4px" w="100%">
                      <Input
                        fontSize="16px"
                        border="1px solid"
                        disabled={isSubmitting}
                        borderColor={errors.jobTitle ? 'error.500' : 'neutrals.200'}
                        borderRadius="8px"
                        {...field}
                        placeholder="Enter job title"
                      />
                    </Box>
                  )}
                />
              </Field>

              {/* Description */}
              <Field label={<Text fontSize="16px">Interview Description</Text>}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <Box px="4px" w="100%">
                      <Textarea
                        border="1px solid"
                        borderColor="neutrals.200"
                        borderRadius="12px"
                        fontSize="16px"
                        minBlockSize="150px"
                        disabled={isSubmitting}
                        {...field}
                        placeholder="Add additional instructions for the candidate as necessary."
                        rows={3}
                      />
                    </Box>
                  )}
                />
              </Field>

              {/* Questions */}
              <Field label={<RequiredFieldLabel>Interview Questions</RequiredFieldLabel>}>
                <Stack gap="8px" w="100%">
                  <Text fontSize="14px" color="neutrals.500">
                    Add up to 15 interview questions for this position.
                  </Text>
                  <Box
                    w="100%"
                    border="1px solid"
                    borderColor={
                      submitError?.includes('Maximum of 15 questions')
                        ? 'error.500'
                        : 'neutrals.200'
                    }
                    borderRadius="12px"
                  >
                    <Stack gap="12px">
                      <Flex justify="flex-end" pr="24px" pt="12px">
                        <Button
                          variant="outline"
                          border="1px solid"
                          borderRadius="8px"
                          borderColor="primary.900"
                          color="primary.900"
                          fontSize="14px"
                          _hover={{ bg: 'neutrals.50' }}
                          p="0 8px"
                          disabled={isSubmitting || !canAddQuestion}
                          onClick={handleAddQuestion}
                        >
                          <Plus size={16} />
                          Add a question
                        </Button>
                      </Flex>
                      <Separator />
                      {fields.map((field, index) => (
                        <div key={field.id}>
                          <QuestionField
                            index={index}
                            field={field}
                            control={control}
                            errors={errors}
                            isSubmitting={isSubmitting}
                            onRemove={handleRemoveQuestion}
                            canRemove={fields.length > 1}
                          />
                          {index < fields.length - 1 && <Separator orientation="horizontal" />}
                        </div>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Field>

              {submitError && (
                <Text color="error.500" fontSize="16px">
                  {submitError}
                </Text>
              )}

              {/* Action Buttons */}
              <HStack justify="flex-end" gap={3}>
                <Button
                  variant="outline"
                  border="1px solid"
                  borderColor="primary.900"
                  color="primary.900"
                  bg="neutrals.0"
                  borderRadius="8px"
                  fontSize="16px"
                  _hover={{ bg: 'neutrals.100' }}
                  p="0 48px"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  borderRadius="8px"
                  bg="primary.900"
                  color="neutrals.0"
                  p="12px 48px"
                  fontSize="16px"
                  _hover={{ bg: 'primary.700' }}
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting || !isFormValid}
                >
                  {isEditing ? 'Update' : 'Create New'}
                </Button>
              </HStack>
            </Stack>
          )}
        </form>
      </Box>

      {/* Import Question Bank Dialog */}
      <ImportQuestionBankDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImport={handleImport}
      />
    </>
  );
}

export default memo(QuestionBankEditor);
